import React, { useCallback, useRef, useState } from 'react'

import { layout } from '../value_bridge/waterfallGeometry.js'

// EVA-303: the Value Bridge waterfall, drawn by hand.
//
// No charting library. The evaluation that settled it is in tmp/EVA-303: no
// library computes a waterfall (they all want [lo, hi] per bar, which is the
// part that carries the meaning), only two candidates rendered at all under
// EVA's node harness, and the one that looked closest silently ignored the
// y-domain and drew a GBP 200k movement 0.41px tall on a GBP 127m opening. The
// arithmetic is in waterfallGeometry.js, where spec/javascript can assert the
// coordinates; this file is markup and interaction only.
//
// ACCESSIBILITY. The drawing is `aria-hidden`; movement labels beneath it are
// real `<button>`s carrying the full accessible name, while opening and closing
// totals are static text. An SVG `<g>` with `role="button"` looks equivalent and
// is not: `role="img"` on the parent makes every descendant presentational, so
// those labels are discarded — while `tabIndex` still puts each one in the tab
// order. That combination gives a keyboard user twenty-six focus stops that
// announce nothing. The analysis below remains the accessible equivalent.
//
// Two things the axis does that a reader must be told about:
//   * it does not start at zero, and it says so;
//   * the two totals are drawn from the domain floor, so their HEIGHTS are not
//     proportional to their values. Only the movements between them are.
export default function ValueBridgeWaterfall({
  bars,
  labelFor,
  formatValue,
  ariaLabelFor,
  selectedId,
  onSelect,
  emptyLabel,
  axisNote
}) {
  const [width, setWidth] = useState(0)
  const observerRef = useRef(null)

  // A callback ref, not a layout effect with `[]` deps. The node it has to
  // measure genuinely changes: the unavailable state and the chart are
  // different elements, so an observer installed once at mount goes on
  // watching a node that is no longer in the document.
  const measureRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    if (!node) return

    setWidth(node.clientWidth)
    // Not window.resize: the tab sits in a Bootstrap column that changes width
    // when the header's version dropdown appears, with no window event.
    if (typeof ResizeObserver === 'undefined') return
    observerRef.current = new ResizeObserver(() => setWidth(node.clientWidth))
    observerRef.current.observe(node)
  }, [])

  const plot = layout(bars, width || undefined)
  const activate = useCallback((id) => { if (onSelect) onSelect(id) }, [onSelect])

  if (!plot.available) {
    return (
      <div className="vb-chart-empty text-muted text-center py-5" ref={measureRef}>
        {emptyLabel}
      </div>
    )
  }

  return (
    <div className="vb-chart">
      <div className="vb-chart-scroll" ref={measureRef}>
        <div className="vb-chart-canvas" style={{ width: `${plot.canvasWidth}px` }}>
          <svg
            width={plot.canvasWidth}
            height={plot.height}
            aria-hidden="true"
            className="vb-chart-svg"
          >
            <line
              className="vb-chart-axis"
              x1={0}
              x2={plot.canvasWidth}
              y1={plot.plotBottom}
              y2={plot.plotBottom}
            />

            {plot.bars.map((bar) => {
              const total = bar.kind === 'total'
              const interactive = !total
              const classes = [
                'vb-bar',
                total ? 'vb-bar--total' : `vb-bar--${bar.sign}`,
                interactive ? '' : 'vb-bar--static',
                bar.id === selectedId ? 'vb-bar--selected' : ''
              ].filter(Boolean).join(' ')

              return (
                <g
                  key={bar.id}
                  className={classes}
                  data-bar-id={bar.id}
                  onClick={interactive ? () => activate(bar.id) : undefined}
                >
                  <rect x={bar.x} y={bar.y} width={bar.width} height={bar.height} rx={2} />
                  {/* The 32x32 target, transparent. A 2px bar is not clickable
                      and a pointer-sized one is not reliably so either.
                      `fill: none` would not receive the click. */}
                  <rect
                    className="vb-bar-hit"
                    x={bar.hitX}
                    y={bar.hitY}
                    width={bar.hitWidth}
                    height={bar.hitHeight}
                  />
                  <text className="vb-bar-value" x={bar.centerX} y={bar.labelY}>
                    {formatValue(bar)}
                  </text>
                </g>
              )
            })}
          </svg>

          {/* Category labels are HTML, not SVG <text>: these are phrases, and
              SVG has neither wrapping nor ellipsis. Movement labels are the
              chart's only keyboard surface — see the note at the top. */}
          <div className="vb-chart-labels">
            {plot.bars.map((bar) => {
              const common = {
                'data-label-for': bar.id,
                className: `vb-chart-label${bar.id === selectedId ? ' vb-chart-label--selected' : ''}`,
                style: { left: `${bar.centerX}px`, width: `${plot.slotWidth}px` },
                title: labelFor(bar)
              }

              if (bar.kind === 'total') {
                return (
                  <span
                    key={bar.id}
                    {...common}
                    className={`${common.className} vb-chart-label--static`}
                  >
                    {labelFor(bar)}
                  </span>
                )
              }

              return (
                <button
                  key={bar.id}
                  {...common}
                  type="button"
                  aria-label={ariaLabelFor(bar)}
                  aria-pressed={bar.id === selectedId}
                  onClick={() => activate(bar.id)}
                >
                  {labelFor(bar)}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <div className="vb-chart-note" role="note">
        <i className="bi bi-info-circle" aria-hidden="true" />
        <span>{axisNote}</span>
      </div>
    </div>
  )
}
